import {
  Button,
  Checkbox,
  IconButton,
} from '@fattureincloud/fic-design-system'
import {
  faComments,
  faEdit,
  faExternalLinkAlt,
  faPrint,
  faSave,
  faTrash,
} from '@fortawesome/pro-light-svg-icons'
import React, { FC, useEffect, useState } from 'react'

import { printArticlePdf } from '../../common/utils'
import { locale } from '../../locales/locale'
import { useAppSelector } from '../../redux/hooks'
import { ArticleInfoBox, Confirm, HelpModal, SideBarTitle } from '../common.components'
import { ArticleError, ArticleInfo, StatisticsArticle } from '../definitions'
import EditorModeToggle from '../EditorMode/EditorModeToggle'
import {
  Actions,
  ArticleActionsBox,
  ArticleOptions,
  ArticleTitleInsert,
} from './article.components'
import NegativeCommentsModal from './NegativeCommentsModal'
import ReadonlyArticleActions from './ReadonlyArticleActions'

interface Props {
  statistics: StatisticsArticle
  subArticle: boolean
  isExternal: boolean
  viewportWidth: number
  editedArticle: ArticleInfo
  onSaveArticleDetails: (
    visCompany: boolean,
    visAccountant: boolean,
    visDraft: boolean,
    title: string
  ) => void
  onDeleteArticle: () => void
  isDeleting: boolean
  isSaving: boolean
  onSaveArticle: () => void
}

const ArticleLateralActions: FC<Props> = (props: Props) => {
  const [negativeModalOpen, setNegativeModalOpen] = useState<boolean>(false)
  const [detailsOpen, setDetailsOpen] = useState<boolean>(false)
  const [title, setTitle] = useState<string>(props.editedArticle.title)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false)
  const [articleError, setArticleError] = useState<ArticleError>({
    title: undefined,
  })
  const [visibility, setVisibility] = useState<{
    accountant: boolean
    company: boolean
    draft: boolean
  }>({
    accountant: props.editedArticle.vis_accountant,
    company: props.editedArticle.vis_company,
    draft: props.editedArticle.is_draft,
  })

  const editorModeActive = useAppSelector((state) => state.editor.value)
  const realm = useAppSelector((state) => state.realm.value)

  const checkTitleError = () => {
    const errTitle =
      title === ''
        ? {
          id: 'generic',
          message: locale('Completare il campo'),
        }
        : undefined
    setArticleError({ title: errTitle })
    if (!errTitle) {
      props.onSaveArticleDetails(
        visibility.company,
        visibility.accountant,
        visibility.draft,
        title
      )
      setDetailsOpen(false)
    }
  }

  useEffect(() => {
    setTitle(props.editedArticle.title)
    setVisibility({
      accountant: props.editedArticle.vis_accountant,
      company: props.editedArticle.vis_company,
      draft: props.editedArticle.is_draft,
    })
  }, [props.editedArticle])

  const renderActions = (
    <>
      <EditorModeToggle />
      {!editorModeActive && (
        <ReadonlyArticleActions
          editedArticlePermalink={props.editedArticle.permalink}
          isExternal={props.isExternal}
          realm={props.editedArticle.realm}
          subArticle={props.subArticle}
          viewportWidth={props.viewportWidth}
          articleId={props.editedArticle.id}
        />
      )}
      {editorModeActive && (
        <Actions>
          <Button
            type={'text'}
            iconLeft={{ icon: faEdit }}
            onClick={() => setDetailsOpen(true)}
          />
          <Button
            type={'text'}
            onClick={() => setDeleteConfirmOpen(true)}
            iconLeft={{ icon: faTrash }}
            isLoading={props.isDeleting}
          />
          {props.viewportWidth > 768 && (
            <Button
              type={'text'}
              iconLeft={{ icon: faPrint }}
              onClick={() => printArticlePdf(props.editedArticle.id)}
            />
          )}
          {!props.subArticle && !props.isExternal && (
            <Button
              type={'text'}
              iconLeft={{ icon: faExternalLinkAlt }}
              href={'/help/articolo/' + props.editedArticle.permalink}
              target={'_blank'}
            />
          )}
          <Button
            type={'text'}
            onClick={props.onSaveArticle}
            iconLeft={{ icon: faSave }}
            isLoading={props.isSaving}
          />
        </Actions>
      )}
    </>
  )
  const renderStatistics = (
    <>
      {props.statistics && (
        <>
          {negativeModalOpen &&
            props.statistics.negative_comments?.length > 0 && (
              <NegativeCommentsModal
                data={props.statistics.negative_comments}
                isOpen={negativeModalOpen}
                setIsOpen={setNegativeModalOpen}
              />
            )}
          <p>
            <b>Visualizzazioni:</b> {props.statistics.views}
          </p>
          <p>
            <b>Feedbacks:</b> {props.statistics.feedbacks}
          </p>
          <p>
            <b>Utile:</b>{' '}
            {Number(props.statistics.useful).toLocaleString(undefined, {
              currency: 'EUR',
              style: 'percent',
              minimumFractionDigits: 1,
            })}
          </p>
          {props.statistics.negative_comments?.length > 0 && (
            <IconButton
              onClick={() => setNegativeModalOpen(true)}
              icon={{ icon: faComments, size: '1x', color: 'blue' }}
            />
          )}
        </>
      )}
    </>
  )

  const visibilityOptions = (
    <ArticleOptions>

      {realm !== 'resellers' &&
        <>
          <Checkbox
            text={locale('Visibile Aziende')}
            value={visibility.company}
            onClick={() =>
              setVisibility({
                ...visibility,
                company: !visibility.company,
              })
            }
          />
          <Checkbox
            text={locale('Visibile Commercialisti')}
            value={visibility.accountant}
            onClick={() =>
              setVisibility({
                ...visibility,
                accountant: !visibility.accountant,
              })
            }
          />
        </>
      }
      <Checkbox
        text={locale('Bozza')}
        value={visibility.draft}
        onClick={() =>
          setVisibility({
            ...visibility,
            draft: !visibility.draft,
          })
        }
      />
    </ArticleOptions>
  )

  const articleTitle = (
    <ArticleTitleInsert
      required={true}
      autoFocus={true}
      label={locale('Titolo')}
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      status={articleError.title ? 'error' : 'normal'}
      helper={
        articleError.title
          ? {
            message: articleError.title.message,
            status: 'error',
          }
          : undefined
      }
    />
  )

  const detailsModal = (
    <HelpModal
      title={locale('Dettagli articolo')}
      footerButtons={[
        {
          text: locale('Annulla'),
          color: 'grey',
          onClick: () => setDetailsOpen(false),
        },
        { text: locale('Salva'), color: 'green', onClick: checkTitleError },
      ]}
    >
      <>
        <span>
          <b>ID:</b> {props.editedArticle.id}
        </span>
        {props.editedArticle.updated_at && (
          <span>
            <b>{locale(' Ultima modifica:')}</b> {props.editedArticle.updated_at}
          </span>
        )}
        {props.editedArticle.updated_by_name && (
          <span>
            <b>{locale(' Utente:')}</b> {props.editedArticle.updated_by_name}
          </span>
        )}
        {props.editedArticle.created_by_name && props.editedArticle.created_at && (
          <>
            <span>
              <b>{locale('Creato da:')}</b> {props.editedArticle.created_by_name} - {locale('il')} {props.editedArticle.created_at}
            </span>
          </>
        )}
      </>
      {visibilityOptions}
      {articleTitle}
    </HelpModal>
  )

  return (
    <>
      {deleteConfirmOpen && editorModeActive && (
        <Confirm
          title={locale('Eliminare articolo')}
          type={'warning'}
          question={locale("Vuoi eliminare definitivamente l'articolo?")}
          description={
            locale("Procedendo con l'operazione") + ', ' +
            locale("tutte le informazioni relative all'articolo verranno eliminate definitivamente.")
          }
          actionText={locale('Elimina')}
          cancelText={locale('Annulla')}
          onAction={props.onDeleteArticle}
          isOpen={deleteConfirmOpen}
          setIsOpen={setDeleteConfirmOpen}
        />
      )}
      <ArticleActionsBox>
        {detailsOpen && editorModeActive && detailsModal}
        {renderActions}
        {editorModeActive && (
          <ArticleInfoBox>
            <>
              <SideBarTitle>{locale('Statistiche anno corrente')}</SideBarTitle>
              {renderStatistics}
            </>
          </ArticleInfoBox>
        )}
      </ArticleActionsBox>
    </>
  )
}

export default ArticleLateralActions
